import useAlerts from './useAlerts';
import useGlobalUrl from '@/composables/useGlobalUrl';
import useSilentAccessToken from './useSilentAccessToken';

const { addAlert } = useAlerts();
const { contactFileUrl } = useGlobalUrl();

export default function useContactFiles() {
  async function uploadContactFiles(files, entityid, type, showCompletedAlert) {
    const { getAccessToken } = useSilentAccessToken();
    const token = await getAccessToken();

    try {
      for (var i = 0; i < files.length; i++) {
        if (files && files[i]) {
          const formData = new FormData();
          formData.append('file', files[i]);

          // Use contactid or companyid based on type
          const idParam = type === 'contact' ? 'contactid' : 'companyid';
          const result = await fetch(`${contactFileUrl}/upload?${idParam}=${entityid}`, {
            method: 'POST',
            headers: {
              Authorization: token ? `Bearer ${token}` : '',
            },
            body: formData,
          });
          if (!result.ok) throw new Error('Error saving file');
        }
      }
      if (showCompletedAlert) addAlert('File saved', 'low', 3);
    } catch (error) {
      console.error(error);
      addAlert('Error saving file', 'high', 4);
    }
  }

  async function deleteFile(entityid, type, filename) {
    const { getAccessToken } = useSilentAccessToken();
    const token = await getAccessToken();

    try {
      // Use contactid or companyid based on type
      const idParam = type === 'contact' ? 'contactid' : 'companyid';
      const result = await fetch(`${contactFileUrl}/delete?${idParam}=${entityid}&filename=${encodeURIComponent(filename)}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      if (!result.ok) throw new Error('Error deleting file');
    } catch (error) {
      console.error(error);
      addAlert('Error deleting file', 'high', 4);
    }
  }

  async function renameFile(entityid, type, filename, newFilename) {
    const { getAccessToken } = useSilentAccessToken();
    const token = await getAccessToken();

    try {
      // Use contactid or companyid based on type
      const idParam = type === 'contact' ? 'contactid' : 'companyid';
      const result = await fetch(`${contactFileUrl}/rename?${idParam}=${entityid}&filename=${encodeURIComponent(filename)}&newfilename=${encodeURIComponent(newFilename)}`, {
        method: 'POST',
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
        },
      });
      if (!result.ok) throw new Error('Error renaming file');
    } catch (error) {
      console.error(error);
      addAlert('Error renaming file', 'high', 4);
      return false;
    }
    return true;
  }

  async function getFile(entityid, type, filename) {
    const { getAccessToken } = useSilentAccessToken();
    const token = await getAccessToken();

    try {
      // Fetch the file with Bearer token
      const response = await fetch(`${contactFileUrl}/download`, {
        method: 'POST',
        body: JSON.stringify({
          entityid,
          type,
          filename,
        }),
        headers: {
          Authorization: token ? `Bearer ${token}` : '',
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) throw new Error('Error downloading file');

      // Get the file as a blob
      const blob = await response.blob();

      // Create a temporary link element to trigger download
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      // Clean up the blob URL
      URL.revokeObjectURL(link.href);
    } catch (error) {
      console.error(error);
      addAlert('Error downloading file', 'high', 4);
    }
  }

  return { uploadContactFiles, deleteFile, renameFile, getFile };
}
